import { useState, useEffect, useRef } from "react";

// ─── SECURE API CALL ──────────────────────────────────────────────────────────
// All Claude API calls go through /api/claude (our Vercel serverless proxy).
// The real Anthropic API key never touches the browser.
async function callClaude(body) {
  const res = await fetch("/api/claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}
async function searchFDIC(query) {
  const isCert = /^\d+$/.test(query.trim());
  // Wildcard name filter is more reliable than the search= parameter
  const nameFilter = `ACTIVE%3A1%20AND%20NAME%3A%2A${encodeURIComponent(query.trim())}%2A`;
  const url = isCert
    ? `https://banks.data.fdic.gov/api/institutions?filters=CERT%3A${query.trim()}&fields=NAME,CERT,CITY,STALP,ASSET,ACTIVE&limit=5`
    : `https://banks.data.fdic.gov/api/institutions?filters=${nameFilter}&fields=NAME,CERT,CITY,STALP,ASSET,ACTIVE&limit=8&sort_by=ASSET&sort_order=DESC`;
  const res = await fetch(url);
  const data = await res.json();
  return (data.data || []).map(d => ({
    name: d.data.NAME, cert: String(d.data.CERT), city: d.data.CITY,
    state: d.data.STALP, assets: Math.round(d.data.ASSET / 1000), type: "bank", id: `fdic-${d.data.CERT}`,
  }));
}

// ─── Shared scoring function ──────────────────────────────────────────────────
// All benchmarks derived from avatar, which is computed from real anchor FDIC data.
// Proximity scoring: closer to anchor average = higher score.
// ROA 20 | Asset CAGR 20 | Deposit CAGR 25 | Asset size 15 | Efficiency 15 | Branch count 5 = 100

// Helper: score a metric by proximity to an anchor average
// Returns 0–maxPts. Full points within 15% of avg, sliding scale to 50% of avg, 0 beyond.
function proximityScore(value, anchorAvg, maxPts, higherIsBetter = true) {
  if (value === null) return { pts: 0, pct: null };
  if (anchorAvg === null) return { pts: Math.round(maxPts * 0.5), pct: null };
  const ratio = higherIsBetter
    ? value / anchorAvg          // >1 means prospect is better than anchors
    : anchorAvg / value;         // for efficiency: lower is better, so invert
  let pts;
  if (ratio >= 1.15)       pts = maxPts;           // 15%+ better than anchors → full
  else if (ratio >= 1.0)   pts = maxPts;           // at or above anchor avg → full
  else if (ratio >= 0.85)  pts = Math.round(maxPts * 0.7);  // within 15% below → 70%
  else if (ratio >= 0.65)  pts = Math.round(maxPts * 0.4);  // within 35% below → 40%
  else if (ratio >= 0.5)   pts = Math.round(maxPts * 0.15); // within 50% below → 15%
  else                     pts = 0;
  return { pts, pct: Math.round((ratio - 1) * 100) };
}

// CAGR proximity: scores based on distance from anchor avg CAGR
// Growth higher than anchors scores full. Near-zero scores partial. Negative scores zero.
function cagrScore(value, anchorAvg, maxPts) {
  if (value === null) return { pts: 0 }; // null = data unavailable, skip this factor
  // If no anchor data, use absolute thresholds as fallback
  if (anchorAvg === null) {
    if (value >= 15)     return { pts: maxPts };
    if (value >= 8)      return { pts: Math.round(maxPts * 0.7) };
    if (value >= 4)      return { pts: Math.round(maxPts * 0.4) };
    if (value >= 1)      return { pts: Math.round(maxPts * 0.15) };
    return { pts: 0 };
  }
  // With anchor data: score relative to anchor average
  const diff = value - anchorAvg; // positive = growing faster than anchors
  if (diff >= 5)        return { pts: maxPts };
  if (diff >= 0)        return { pts: maxPts };              // at or above avg → full
  if (diff >= -3)       return { pts: Math.round(maxPts * 0.65) };
  if (diff >= -7)       return { pts: Math.round(maxPts * 0.35) };
  if (value > 0)        return { pts: Math.round(maxPts * 0.15) };
  return { pts: 0 };
}

function scoreInstitution({
  assetsMillion, roa, efficiencyRatio, branches, avatar,
  assetCAGR, depositCAGR,
}) {
  let score = 0;
  let maxPossible = 0; // track max achievable based on available data
  const reasons = [];
  const penalties = [];

  // ── ROA — 20 pts — ability to pay, benchmarked to anchor average ───────────
  if (roa !== null) {
    maxPossible += 20;
    const { pts, pct } = proximityScore(roa, avatar.avgROA, 20, true);
    score += pts;
    const benchStr = avatar.avgROA !== null ? ` (anchor avg: ${avatar.avgROA.toFixed(2)}%)` : "";
    if (pts >= 20)     reasons.push(`ROA ${roa.toFixed(2)}% meets or exceeds anchor client average${benchStr}`);
    else if (pts >= 10) reasons.push(`ROA ${roa.toFixed(2)}% is near anchor client average${benchStr}`);
    else if (pts > 0)  reasons.push(`ROA ${roa.toFixed(2)}% is below anchor average${benchStr}`);
    else               penalties.push(`ROA ${roa.toFixed(2)}% is well below anchor average${benchStr}`);
  }

  // ── Asset CAGR — 20 pts — growth complexity, benchmarked to anchors ────────
  if (assetCAGR !== null) {
    maxPossible += 20;
    const { pts } = cagrScore(assetCAGR, avatar.avgAssetCAGR, 20);
    score += pts;
    const benchStr = avatar.avgAssetCAGR !== null ? ` (anchor avg: ${avatar.avgAssetCAGR.toFixed(1)}%/yr)` : "";
    if (pts >= 20)      reasons.push(`Asset CAGR ${assetCAGR.toFixed(1)}%/yr matches or exceeds anchor growth rate${benchStr}`);
    else if (pts >= 10) reasons.push(`Asset CAGR ${assetCAGR.toFixed(1)}%/yr is near anchor growth rate${benchStr}`);
    else if (pts > 0)   reasons.push(`Asset CAGR ${assetCAGR.toFixed(1)}%/yr — below anchor growth rate${benchStr}`);
    else                penalties.push(`Asset CAGR ${assetCAGR.toFixed(1)}%/yr — significantly below anchor growth rate${benchStr}`);
  }

  // ── Deposit CAGR — 25 pts — attack surface, benchmarked to anchors ─────────
  if (depositCAGR !== null) {
    maxPossible += 25;
    const { pts } = cagrScore(depositCAGR, avatar.avgDepositCAGR, 25);
    score += pts;
    const benchStr = avatar.avgDepositCAGR !== null ? ` (anchor avg: ${avatar.avgDepositCAGR.toFixed(1)}%/yr)` : "";
    if (pts >= 20)      reasons.push(`Deposit CAGR ${depositCAGR.toFixed(1)}%/yr matches or exceeds anchor deposit growth${benchStr}`);
    else if (pts >= 10) reasons.push(`Deposit CAGR ${depositCAGR.toFixed(1)}%/yr is near anchor deposit growth${benchStr}`);
    else if (pts > 0)   reasons.push(`Deposit CAGR ${depositCAGR.toFixed(1)}%/yr — below anchor deposit growth${benchStr}`);
    else                penalties.push(`Deposit CAGR ${depositCAGR.toFixed(1)}%/yr — significantly below anchor deposit growth${benchStr}`);
  }

  // ── Asset size — 15 pts — range fit ────────────────────────────────────────
  maxPossible += 15;
  if (assetsMillion >= avatar.assetMin && assetsMillion <= avatar.assetMax) {
    score += 15;
    reasons.push(`$${assetsMillion}M assets within ideal range ($${avatar.assetMin}M–$${avatar.assetMax}M)`);
  } else if (assetsMillion >= avatar.assetMin * 0.75 && assetsMillion <= avatar.assetMax * 1.25) {
    score += 8;
    reasons.push(`$${assetsMillion}M assets near ideal range ($${avatar.assetMin}M–$${avatar.assetMax}M)`);
  } else {
    penalties.push(`$${assetsMillion}M assets outside ideal range`);
  }

  // ── Efficiency ratio — 15 pts — lower is better, benchmarked to anchors ────
  if (efficiencyRatio !== null) {
    maxPossible += 15;
    const { pts } = proximityScore(efficiencyRatio, avatar.avgEfficiency, 15, false);
    score += pts;
    const benchStr = avatar.avgEfficiency !== null ? ` (anchor avg: ${avatar.avgEfficiency.toFixed(1)}%)` : "";
    if (pts >= 15)      reasons.push(`Efficiency ratio ${efficiencyRatio.toFixed(1)}% is as good or better than anchor clients${benchStr}`);
    else if (pts >= 8)  reasons.push(`Efficiency ratio ${efficiencyRatio.toFixed(1)}% is near anchor client average${benchStr}`);
    else if (pts > 0)   reasons.push(`Efficiency ratio ${efficiencyRatio.toFixed(1)}% is above anchor average${benchStr}`);
    else                penalties.push(`Efficiency ratio ${efficiencyRatio.toFixed(1)}% is well above anchor average${benchStr}`);
  }

  // Branch delta removed — FDIC API does not expose historical branch counts

  // ── Branch count — 5 pts — operational scale fit ───────────────────────────
  maxPossible += 5;
  if (branches !== null && branches >= avatar.minBranches && branches <= avatar.maxBranches) {
    score += 5;
    reasons.push(`${branches} branches matches operational scale of anchor clients`);
  }

  // Normalize: scale score to 100 based on what data was actually available
  // This prevents banks from being penalized for missing API data
  const normalizedScore = maxPossible > 0 ? Math.round((score / maxPossible) * 100) : 0;

  if (normalizedScore < 60 && penalties.length > 0) {
    penalties.forEach(p => reasons.push(`⚠ ${p}`));
  }

  return { score: Math.min(normalizedScore, 99), reasons: reasons.slice(0, 6) };
}

// ─── FDIC Prospect Search ─────────────────────────────────────────────────────
// Three API calls run in parallel after the institution list is fetched:
//   • Current financials  — ROA, efficiency ratio
//   • Historical financials (3 years ago) — asset CAGR, deposit CAGR, branch delta
async function fetchFDICProspects(stateList, avatar) {
  const stateFilter = stateList.map(s => `STALP%3A${s}`).join("%20OR%20");
  const assetMinK = Math.round(avatar.assetMin * 0.5 * 1000);
  const assetMaxK = Math.round(avatar.assetMax * 1.5 * 1000);

  // Step 1 — Institution list with address, website, current snapshot
  const instUrl = `https://banks.data.fdic.gov/api/institutions?` +
    `filters=ACTIVE%3A1%20AND%20(${stateFilter})%20AND%20ASSET%3A%5B${assetMinK}%20TO%20${assetMaxK}%5D` +
    `&fields=NAME,CERT,CITY,STALP,ASSET,ROA,OFFICES,ACTIVE,ADDRESS,ZIP,WEBADDR` +
    `&limit=150&sort_by=ASSET&sort_order=DESC`;

  const instRes = await fetch(instUrl);
  if (!instRes.ok) throw new Error(`FDIC institutions API error: ${instRes.status}`);
  const instData = await instRes.json();
  const institutions = instData.data || [];
  if (institutions.length === 0) return [];

  const certs = institutions.map(d => d.data.CERT).join("%20OR%20CERT%3A");

  // Step 2 — Run current financials + historical financials in parallel
  // Historical target: most recent report on or before Q4 three years ago
  const now = new Date();
  const threeYearsAgo = `${now.getFullYear() - 3}1231`;

  // Batch certs into groups of 25 to avoid URL length limits
  const certList = institutions.map(d => d.data.CERT);
  const batchSize = 25;
  const batches = [];
  for (let i = 0; i < certList.length; i += batchSize) {
    batches.push(certList.slice(i, i + batchSize));
  }

  const fetchBatch = (certBatch, extraFilter = "") => {
    const certFilter = certBatch.map(c => `CERT%3A${c}`).join("%20OR%20");
    const filter = extraFilter ? `(${certFilter})%20AND%20${extraFilter}` : `(${certFilter})`;
    return fetch(`https://banks.data.fdic.gov/api/financials?filters=${filter}` +
      `&fields=CERT,REPDTE,EEFFR,ROA,DEP,ASSET&limit=100&sort_by=REPDTE&sort_order=DESC`)
      .then(r => r.json()).catch(() => ({ data: [] }));
  };

  const histFilter = `REPDTE%3A%5B${now.getFullYear() - 3}0101%20TO%20${threeYearsAgo}%5D`;

  // Fetch current and historical for all batches in parallel
  const allBatchResults = await Promise.all(
    batches.flatMap(batch => [
      fetchBatch(batch),
      fetchBatch(batch, histFilter),
    ])
  );

  // Split results back into current and historical
  const currentMap = {};
  const historicalMap = {};
  for (let i = 0; i < batches.length; i++) {
    const curData = allBatchResults[i * 2]?.data || [];
    const histData = allBatchResults[i * 2 + 1]?.data || [];
    for (const row of curData) {
      const c = String(row.data.CERT);
      if (!currentMap[c]) currentMap[c] = row.data;
    }
    for (const row of histData) {
      const c = String(row.data.CERT);
      if (!historicalMap[c]) historicalMap[c] = row.data;
    }
  }

  return institutions.map(d => {
    const inst = d.data;
    const cert = String(inst.CERT);
    const cur = currentMap[cert] || {};
    const hist = historicalMap[cert] || {};

    const assetsMillion = Math.round(inst.ASSET / 1000);

    // ROA from institution snapshot (most reliable) or current financials
    const roa = (inst.ROA != null && inst.ROA !== 0)
      ? parseFloat(inst.ROA)
      : (cur.ROA != null ? parseFloat(cur.ROA) : null);

    // Efficiency ratio from current financials
    let efficiencyRatio = null;
    if (cur.EEFFR != null && cur.EEFFR > 0 && cur.EEFFR < 200) {
      efficiencyRatio = parseFloat(cur.EEFFR);
    }

    // Current branches
    const branches = inst.OFFICES != null ? parseInt(inst.OFFICES) : null;

    // ── 3-year CAGR calculations ──────────────────────────────────────────────
    // CAGR = (current / historical) ^ (1/3) - 1
    let assetCAGR = null;
    let depositCAGR = null;

    if (hist.ASSET != null && hist.ASSET > 0 && cur.ASSET != null && cur.ASSET > 0) {
      assetCAGR = parseFloat(((Math.pow(cur.ASSET / hist.ASSET, 1 / 3) - 1) * 100).toFixed(1));
    } else if (hist.ASSET != null && hist.ASSET > 0) {
      // Fall back to current institution snapshot vs historical
      assetCAGR = parseFloat(((Math.pow((inst.ASSET) / hist.ASSET, 1 / 3) - 1) * 100).toFixed(1));
    }

    if (hist.DEP != null && hist.DEP > 0 && cur.DEP != null && cur.DEP > 0) {
      depositCAGR = parseFloat(((Math.pow(cur.DEP / hist.DEP, 1 / 3) - 1) * 100).toFixed(1));
    }

    // branchDelta removed — OFFICES not available in financials historical data

    const { score, reasons } = scoreInstitution({
      assetsMillion, roa, efficiencyRatio, branches,
      state: inst.STALP, avatar,
      assetCAGR, depositCAGR,
    });

    // Build clean address string
    const address = [inst.ADDRESS, inst.CITY, inst.STALP, inst.ZIP]
      .filter(Boolean).join(", ");

    // Clean website URL
    let website = inst.WEBADDR || "";
    if (website && !website.startsWith("http")) website = "https://" + website;

    return {
      name: inst.NAME, cert, city: inst.CITY, state: inst.STALP,
      address, website,
      assets: assetsMillion,
      roa: roa !== null ? parseFloat(roa.toFixed(2)) : "N/A",
      efficiencyRatio: efficiencyRatio !== null ? parseFloat(efficiencyRatio.toFixed(1)) : "N/A",
      assetCAGR: assetCAGR !== null ? assetCAGR : "N/A",
      depositCAGR: depositCAGR !== null ? depositCAGR : "N/A",
      branches: branches || "N/A",
      type: "bank", id: `fdic-${cert}`,
      matchScore: score, matchReasons: reasons,
    };
  });
}

// ─── Prospect Search ─────────────────────────────────────────────────────────
async function fetchAllProspects(states, avatar) {
  const stateList = states
    .split(",")
    .map(s => s.trim().toUpperCase())
    .filter(s => s.length === 2);

  if (stateList.length === 0) throw new Error("No valid state codes provided");

  const results = await fetchFDICProspects(stateList, avatar);

  return results
    .filter(i => i.matchScore >= 80)
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, 50);
}


// ─── UI Components ────────────────────────────────────────────────────────────
function ScoreBadge({ score }) {
  const color = score >= 85 ? "#00e5a0" : score >= 70 ? "#f5c842" : "#ff6b6b";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", border: `3px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", color, fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: 13 }}>{score}</div>
      <span style={{ color, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{score >= 85 ? "Strong" : score >= 70 ? "Moderate" : "Weak"}</span>
    </div>
  );
}

function MetricPill({ label, value, unit = "" }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 14px" }}>
      <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</div>
      <div style={{ fontSize: 15, color: "#e8e4dc", fontFamily: "'DM Mono',monospace", fontWeight: 600, marginTop: 2 }}>{value}{unit}</div>
    </div>
  );
}

function InstitutionTag({ inst, onRemove }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "rgba(0,180,216,0.08)", border: "1px solid rgba(0,180,216,0.25)", borderRadius: 10, padding: "9px 14px", margin: "0 6px 8px 0" }}>
      <div>
        <div style={{ fontSize: 13, color: "#00b4d8", fontWeight: 600 }}>{inst.name}</div>
        <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>FDIC{inst.cert && inst.cert !== "manual" ? ` #${inst.cert}` : ""}{inst.city ? ` · ${inst.city}` : ""}{inst.state ? `, ${inst.state}` : ""}{inst.assets > 0 ? ` · $${inst.assets}M` : ""}</div>
      </div>
      <button onClick={() => onRemove(inst.id)} style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}

function InstitutionSearch({ onSelect, existingIds }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [apiError, setApiError] = useState(false);
  const debounce = useRef(null);
  const wrapper = useRef(null);

  useEffect(() => {
    const close = (e) => { if (wrapper.current && !wrapper.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) { setResults([]); setOpen(false); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setSearching(true); setApiError(false);
      try {
        const res = await searchFDIC(query);
        setResults(res.filter(r => !existingIds.includes(r.id)));
        setOpen(true);
      } catch { setApiError(true); setOpen(true); setResults([]); }
      setSearching(false);
    }, 380);
    return () => clearTimeout(debounce.current);
  }, [query, existingIds.join(",")]);

  const pick = (inst) => { onSelect(inst); setQuery(""); setResults([]); setOpen(false); };
  const addManual = () => pick({ name: query.trim(), cert: "manual", city: "", state: "", assets: 0, type: "bank", id: `manual-${Date.now()}` });

  return (
    <div ref={wrapper} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <input value={query} onChange={e => setQuery(e.target.value)} onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search by bank name or FDIC certificate number"
          style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, padding: "13px 44px 13px 16px", color: "#e8e4dc", fontSize: 14, outline: "none", fontFamily: "'DM Sans',sans-serif" }} />
        <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 15, color: "#555" }}>{searching ? "⟳" : "⌕"}</span>
      </div>
      <p style={{ fontSize: 11, color: "#555", marginTop: 6 }}>Search by name or enter an FDIC certificate number directly · Live lookup via FDIC BankFind</p>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 300, background: "#16161a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, boxShadow: "0 20px 50px rgba(0,0,0,0.7)", overflow: "hidden" }}>
          {apiError && (
            <div style={{ padding: "16px 20px" }}>
              <p style={{ color: "#f5c842", fontSize: 13, margin: 0 }}>⚠ FDIC API unavailable. Try again or add manually.</p>
              {query.trim() && <button onClick={addManual} style={{ marginTop: 10, padding: "7px 14px", borderRadius: 7, background: "rgba(245,200,66,0.1)", border: "1px solid rgba(245,200,66,0.3)", color: "#f5c842", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>+ Add "{query.trim()}" manually</button>}
            </div>
          )}
          {!apiError && results.length === 0 && !searching && (
            <div style={{ padding: "16px 20px" }}>
              <p style={{ color: "#666", fontSize: 13, margin: 0 }}>No results for "{query}".</p>
              {query.trim().length > 2 && <button onClick={addManual} style={{ marginTop: 10, padding: "7px 14px", borderRadius: 7, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#888", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>+ Add "{query.trim()}" manually</button>}
            </div>
          )}
          {!apiError && results.map((r, i) => (
            <div key={r.id} onClick={() => pick(r)} style={{ padding: "12px 20px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: i < results.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div>
                <div style={{ fontSize: 14, color: "#e8e4dc", fontWeight: 500 }}>{r.name}</div>
                <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{r.city}{r.state ? `, ${r.state}` : ""}{r.cert ? ` · FDIC #${r.cert}` : ""}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 16 }}>
                {r.assets > 0 && <div style={{ fontSize: 13, color: "#00e5a0", fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>${r.assets}M</div>}
                <div style={{ fontSize: 11, marginTop: 3, padding: "2px 8px", borderRadius: 4, display: "inline-block", background: "rgba(0,180,216,0.15)", color: "#00b4d8" }}>Bank</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Outreach Modal ───────────────────────────────────────────────────────────

// ─── Avatar Card ──────────────────────────────────────────────────────────────
function AvatarCard({ avatar }) {
  const na = v => v !== null && v !== undefined ? v : "N/A";
  const pct1 = v => v !== null && v !== undefined ? `${v.toFixed(1)}%` : "N/A";
  const pct2 = v => v !== null && v !== undefined ? `${v.toFixed(2)}%` : "N/A";
  return (
    <div style={{ background: "linear-gradient(135deg,rgba(0,229,160,0.06) 0%,transparent 60%)", border: "1px solid rgba(0,229,160,0.25)", borderRadius: 16, padding: 28, marginBottom: 24 }}>
      <h3 style={{ color: "#00e5a0", fontFamily: "'Playfair Display',serif", fontSize: 20, marginBottom: 6, fontWeight: 700 }}>Ideal Client Avatar</h3>
      <p style={{ color: "#666", fontSize: 12, marginBottom: 18 }}>All benchmarks derived from your anchor clients' live FDIC call report data.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 12, marginBottom: 20 }}>
        <MetricPill label="Asset Range" value={`$${avatar.assetMin}M–$${avatar.assetMax}M`} />
        <MetricPill label="Avg ROA" value={pct2(avatar.avgROA)} />
        <MetricPill label="Avg Efficiency" value={pct1(avatar.avgEfficiency)} />
        <MetricPill label="Avg Asset CAGR" value={avatar.avgAssetCAGR !== null ? `${avatar.avgAssetCAGR.toFixed(1)}%/yr` : "N/A"} />
        <MetricPill label="Avg Deposit CAGR" value={avatar.avgDepositCAGR !== null ? `${avatar.avgDepositCAGR.toFixed(1)}%/yr` : "N/A"} />
        <MetricPill label="Branch Range" value={`${avatar.minBranches}–${avatar.maxBranches}`} />
        {avatar.anchorStates.length > 0 && <MetricPill label="Anchor States" value={avatar.anchorStates.join(", ")} />}
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16 }}>
        <p style={{ color: "#999", fontSize: 13, lineHeight: 1.75, margin: 0 }}>
          <span style={{ color: "#00e5a0", fontWeight: 600 }}>Pattern detected from your anchor clients.</span> Prospects are scored by how closely they match these benchmarks — not against fixed thresholds. A bank that looks just like your best clients scores highest.
        </p>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [step, setStep] = useState("anchor");
  const [anchors, setAnchors] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisText, setAnalysisText] = useState("");
  const [analysisDone, setAnalysisDone] = useState(false);
  const [targetGeo, setTargetGeo] = useState("");
  const [prospecting, setProspecting] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [prospectError, setProspectError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [dots, setDots] = useState("");
  // Avatar profile derived from anchor clients — used to score prospects
  // All thresholds are computed from actual anchor FDIC data, not hardcoded
  const [avatar, setAvatar] = useState({
    assetMin: 0, assetMax: 9999999,
    avgROA: null, avgEfficiency: null,
    avgAssetCAGR: null, avgDepositCAGR: null, avgBranchDelta: null,
    minBranches: 0, maxBranches: 999,
    anchorStates: [],
  });
  // Ref holds the latest avatar so runProspecting always gets the fresh value
  // (React setState is async — reading avatar state immediately after setAvatar gives stale data)
  const avatarRef = useRef(null);

  useEffect(() => {
    if (analyzing || prospecting) {
      const t = setInterval(() => setDots(d => d.length >= 3 ? "" : d + "."), 400);
      return () => clearInterval(t);
    }
  }, [analyzing, prospecting]);

  const addAnchor = (inst) => { if (!anchors.find(a => a.id === inst.id)) setAnchors([...anchors, inst]); };
  const removeAnchor = (id) => setAnchors(anchors.filter(a => a.id !== id));

  const runAnalysis = async () => {
    setAnalyzing(true); setAnalysisText("");
    const list = anchors.map(b => `${b.name}${b.city ? ` (${b.city}${b.state ? ", " + b.state : ""})` : ""}${b.assets > 0 ? `, $${b.assets}M assets` : ""}${b.cert && b.cert !== "manual" ? `, FDIC #${b.cert}` : ""}`).join("\n");

    // ── Derive avatar benchmarks from real FDIC data for each anchor ──────────
    const anchorAssets = anchors.filter(a => a.assets > 0).map(a => a.assets);
    const anchorStates = [...new Set(anchors.filter(a => a.state).map(a => a.state))];
    const fdicAnchors = anchors.filter(a => a.cert && a.cert !== "manual");

    let newAvatar = {
      assetMin: anchorAssets.length > 0 ? Math.round(Math.min(...anchorAssets) * 0.7) : 0,
      assetMax: anchorAssets.length > 0 ? Math.round(Math.max(...anchorAssets) * 1.3) : 9999999,
      avgROA: null, avgEfficiency: null,
      avgAssetCAGR: null, avgDepositCAGR: null, avgBranchDelta: null,
      minBranches: 0, maxBranches: 999,
      anchorStates,
    };

    if (fdicAnchors.length > 0) {
      try {
        const now = new Date();
        const threeYearsAgo = `${now.getFullYear() - 3}1231`;
        const histYearStart = `${now.getFullYear() - 3}0101`;
        // Wrap each cert in its own CERT:N term, joined by OR, wrapped in parens
        const certTerms = fdicAnchors.map(a => `CERT%3A${a.cert}`).join("%20OR%20");
        const certFilter = `(${certTerms})`;

        const [curRes, instRes, histRes] = await Promise.allSettled([
          fetch(`https://banks.data.fdic.gov/api/financials?filters=${certFilter}&fields=CERT,REPDTE,ROA,EEFFR,DEP,ASSET&limit=50&sort_by=REPDTE&sort_order=DESC`).then(r => r.json()),
          fetch(`https://banks.data.fdic.gov/api/institutions?filters=${certFilter}&fields=CERT,OFFICES&limit=50`).then(r => r.json()),
          fetch(`https://banks.data.fdic.gov/api/financials?filters=${certFilter}%20AND%20REPDTE%3A%5B${histYearStart}%20TO%20${threeYearsAgo}%5D&fields=CERT,REPDTE,ASSET,DEP&limit=50&sort_by=REPDTE&sort_order=DESC`).then(r => r.json()),
        ]);

        // Most recent record per cert
        const curMap = {};
        if (curRes.status === "fulfilled") {
          for (const row of (curRes.value.data || [])) {
            const c = String(row.data.CERT);
            if (!curMap[c]) curMap[c] = row.data;
          }
        }
        // OFFICES comes from institutions endpoint, not financials
        const instMap = {};
        if (instRes.status === "fulfilled") {
          for (const row of (instRes.value.data || [])) {
            const c = String(row.data.CERT);
            if (!instMap[c]) instMap[c] = row.data;
          }
        }
        const histMap = {};
        if (histRes.status === "fulfilled") {
          for (const row of (histRes.value.data || [])) {
            const c = String(row.data.CERT);
            if (!histMap[c]) histMap[c] = row.data;
          }
        }

        // Compute per-anchor metrics then average across all anchors
        const roaVals = [], effVals = [], assetCAGRVals = [], depCAGRVals = [], branchVals = [];

        for (const anchor of fdicAnchors) {
          const c = anchor.cert;
          const cur = curMap[c] || {};
          const hist = histMap[c] || {};

          if (cur.ROA != null)  roaVals.push(parseFloat(cur.ROA));
          if (cur.EEFFR != null && cur.EEFFR > 0 && cur.EEFFR < 200) effVals.push(parseFloat(cur.EEFFR));
          // OFFICES from institutions endpoint
          const inst = instMap[c] || {};
          if (inst.OFFICES != null) branchVals.push(parseInt(inst.OFFICES));

          if (hist.ASSET > 0 && cur.ASSET > 0) {
            assetCAGRVals.push((Math.pow(cur.ASSET / hist.ASSET, 1 / 3) - 1) * 100);
          }
          if (hist.DEP > 0 && cur.DEP > 0) {
            depCAGRVals.push((Math.pow(cur.DEP / hist.DEP, 1 / 3) - 1) * 100);
          }
          // Branch delta removed — OFFICES not in financials historical endpoint
        }

        const avg = arr => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
        const round1 = v => v !== null ? parseFloat(v.toFixed(1)) : null;
        const round2 = v => v !== null ? parseFloat(v.toFixed(2)) : null;

        if (branchVals.length > 0) {
          const minB = Math.min(...branchVals);
          const maxB = Math.max(...branchVals);
          newAvatar.minBranches = Math.max(0, minB - 2);
          newAvatar.maxBranches = maxB + 2;
        }

        newAvatar = {
          ...newAvatar,
          avgROA:         round2(avg(roaVals)),
          avgEfficiency:  round1(avg(effVals)),
          avgAssetCAGR:   round1(avg(assetCAGRVals)),
          avgDepositCAGR: round1(avg(depCAGRVals)),
          avgBranchDelta: null, // Not available from FDIC API
        };
      } catch (e) {
        console.warn("Could not fetch anchor financials:", e);
      }
    }

    setAvatar(newAvatar);
    avatarRef.current = newAvatar;

    // ── AI narrative analysis ─────────────────────────────────────────────────
    try {
      const data = await callClaude({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        messages: [{ role: "user", content: `You are a financial analyst specializing in community banking. My most profitable and enjoyable clients are:\n${list}\n\nAnalyze what these institutions have in common. Consider asset size, geography, financial health, operational scale, and growth signals. Write 3–4 focused analytical paragraphs identifying the common profile.` }],
      });
      setAnalysisText(data.content?.map(b => b.text || "").join("") || "Analysis complete.");
    } catch { setAnalysisText("Pattern analysis complete. See the avatar summary above."); }
    setAnalyzing(false); setAnalysisDone(true);
  };

  const runProspecting = async () => {
    setProspecting(true);
    setProspectError(null);
    setProspects([]);
    try {
      const currentAvatar = avatarRef.current || avatar;
      const results = await fetchAllProspects(targetGeo, currentAvatar);
      if (results.length === 0) {
        setProspectError(`No institutions found matching your avatar profile in ${targetGeo}. Try broadening the geography or adjusting the states.`);
      } else {
        setProspects(results);
      }
    } catch (e) {
      setProspectError(`Could not reach FDIC API: ${e.message}. Please check your internet connection and try again.`);
    }
    setProspecting(false);
    setStep("prospects");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const btn = (v = "primary") => ({ padding: "12px 24px", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer", border: "none", transition: "all 0.2s", fontFamily: "'DM Sans',sans-serif", ...(v === "primary" ? { background: "linear-gradient(135deg,#00e5a0,#00c48c)", color: "#0d0d0f" } : v === "ghost" ? { background: "transparent", color: "#888", border: "1px solid rgba(255,255,255,0.1)" } : { background: "rgba(0,229,160,0.1)", color: "#00e5a0", border: "1px solid rgba(0,229,160,0.3)" }) });
  const card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 28, marginBottom: 20 };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <div style={{ minHeight: "100vh", background: "#0d0d0f", fontFamily: "'DM Sans',sans-serif", color: "#e8e4dc", paddingBottom: 80 }}>
        <header style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", padding: "24px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(0,0,0,0.5)", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#00e5a0,#00b4d8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚡</div>
            <div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700 }}>Meridian</div>
              <div style={{ fontSize: 11, color: "#555", letterSpacing: "0.15em", textTransform: "uppercase", fontFamily: "'DM Mono',monospace" }}>Market Intelligence Guru</div>
            </div>
          </div>
          <nav style={{ display: "flex", gap: 4 }}>
            {[["anchor", "1. Anchor Clients"], ["analyze", "2. Pattern Analysis"], ["prospects", "3. Prospect List"]].map(([k, label]) => (
              <button key={k} onClick={() => { setStep(k); window.scrollTo({ top: 0, behavior: "smooth" }); }} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", border: "none", fontFamily: "'DM Sans',sans-serif", background: step === k ? "rgba(0,229,160,0.15)" : "transparent", color: step === k ? "#00e5a0" : "#666", borderBottom: step === k ? "2px solid #00e5a0" : "2px solid transparent", transition: "all 0.2s" }}>{label}</button>
            ))}
          </nav>
        </header>

        <main style={{ maxWidth: 900, margin: "0 auto", padding: "48px 24px" }}>
          {step === "anchor" && (
            <div>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.02em" }}>Define Your Anchor Clients</h1>
              <p style={{ color: "#888", fontSize: 15, marginBottom: 36, lineHeight: 1.6 }}>Search by name or FDIC certificate number to find your best existing clients. Results pull live from the FDIC BankFind database.</p>
              <div style={card}>
                <InstitutionSearch onSelect={addAnchor} existingIds={anchors.map(a => a.id)} />
                {anchors.length > 0 && (
                  <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                    <p style={{ fontSize: 12, color: "#666", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>{anchors.length} institution{anchors.length !== 1 ? "s" : ""} selected</p>
                    <div>{anchors.map(a => <InstitutionTag key={a.id} inst={a} onRemove={removeAnchor} />)}</div>
                  </div>
                )}
              </div>
              {anchors.length >= 2 && <div style={{ textAlign: "right" }}><button style={btn("primary")} onClick={() => { runAnalysis(); setStep("analyze"); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Analyze Pattern →</button></div>}
              {anchors.length === 1 && <p style={{ color: "#666", fontSize: 13, textAlign: "right" }}>Add at least one more institution to detect patterns.</p>}
            </div>
          )}

          {step === "analyze" && (
            <div>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.02em" }}>Pattern Analysis</h1>
              <p style={{ color: "#888", fontSize: 15, marginBottom: 36, lineHeight: 1.6 }}>Reverse-engineering your ideal client avatar from your anchor institutions.</p>
              {analyzing && <div style={{ ...card, textAlign: "center", padding: 56 }}><div style={{ fontSize: 36, marginBottom: 16 }}>🔍</div><p style={{ color: "#00e5a0", fontFamily: "'DM Mono',monospace", fontSize: 14 }}>Analyzing anchor profiles{dots}</p></div>}
              {!analyzing && analysisDone && (
                <>
                  <AvatarCard avatar={avatar} />
                  <div style={card}>
                    <h3 style={{ color: "#e8e4dc", fontSize: 16, fontWeight: 600, marginBottom: 16 }}>AI Pattern Analysis</h3>
                    <p style={{ color: "#aaa", fontSize: 14, lineHeight: 1.85, whiteSpace: "pre-wrap", margin: 0 }}>{analysisText}</p>
                  </div>
                  <div style={{ ...card, background: "rgba(0,229,160,0.03)", borderColor: "rgba(0,229,160,0.15)" }}>
                    <h3 style={{ color: "#00e5a0", fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Set Prospecting Geography</h3>
                    <div style={{ display: "flex", gap: 10 }}>
                      <input value={targetGeo} onChange={e => setTargetGeo(e.target.value)} style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "12px 16px", color: "#e8e4dc", fontSize: 14, outline: "none", fontFamily: "'DM Sans',sans-serif" }} placeholder="e.g. OH, IN, KY, WV" />
                      <button style={btn("primary")} onClick={runProspecting}>{prospecting ? `Scanning${dots}` : "Find Prospects →"}</button>
                    </div>
                    <p style={{ color: "#555", fontSize: 12, marginTop: 8 }}>Enter state abbreviations. The agent will filter FDIC institutions matching your avatar profile.</p>
                  </div>
                </>
              )}
              {!analyzing && !analysisDone && <div style={{ ...card, textAlign: "center", padding: 48 }}><p style={{ color: "#666" }}>Go back to Step 1 to add anchor clients.</p><button style={{ ...btn("ghost"), marginTop: 16 }} onClick={() => { setStep("anchor"); window.scrollTo({ top: 0, behavior: "smooth" }); }}>← Back</button></div>}
            </div>
          )}

          {step === "prospects" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em", margin: 0 }}>Prospect List</h1>
                {prospects.length > 0 && (
                  <button onClick={() => {
                    const headers = ["Name","FDIC #","Street Address","City","State","Zip","Website","Total Assets","ROA (%)","Efficiency Ratio (%)","Asset CAGR 3yr (%)","Deposit CAGR 3yr (%)","Branches","Match Score","Match Reasons","Lead Status"];
                    const rows = prospects.map(p => {
                      // Split address into parts — FDIC format is "123 Main St, City, ST, 12345"
                      const addrParts = (p.address || "").split(",").map(s => s.trim());
                      const street = addrParts[0] || "";
                      const city = addrParts[1] || p.city || "";
                      const state = addrParts[2] || p.state || "";
                      const zip = addrParts[3] || "";
                      // Strip http:// https:// www. from website
                      const domain = (p.website || "").replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
                      // Convert $M to full dollar figure
                      const totalAssets = p.assets !== "N/A" ? `$${(p.assets * 1_000_000).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
                      return [
                        `"${p.name}"`,
                        p.cert,
                        `"${street}"`,
                        `"${city}"`,
                        `"${state}"`,
                        `"${zip}"`,
                        `"${domain}"`,
                        `"${totalAssets}"`,
                        p.roa,
                        p.efficiencyRatio,
                        p.assetCAGR,
                        p.depositCAGR,
                        p.branches,
                        p.matchScore,
                        `"${p.matchReasons.join("; ")}"`,
                        "Cold Outbound Opportunity",
                      ];
                    });
                    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `meridian-prospects-${targetGeo.replace(/[\s,]+/g, "-")}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }} style={{ padding: "10px 22px", borderRadius: 10, background: "linear-gradient(135deg,#00e5a0,#00c48c)", border: "none", color: "#0d0d0f", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontWeight: 700, flexShrink: 0 }}>
                    ⬇ Export CSV
                  </button>
                )}
              </div>
              <p style={{ color: "#888", fontSize: 15, marginBottom: 36, lineHeight: 1.6 }}>
                {prospects.length > 0
                  ? `${prospects.length} banks scoring 80+ in ${targetGeo}, ranked by match score. Export to CSV to share with your sales team.`
                  : `Searching ${targetGeo} for banks matching your avatar profile.`}
              </p>

              {prospecting && <div style={{ ...card, textAlign: "center", padding: 56 }}>
                <div style={{ fontSize: 36, marginBottom: 16 }}>📡</div>
                <p style={{ color: "#00e5a0", fontFamily: "'DM Mono',monospace", fontSize: 14 }}>Scanning FDIC registry for {targetGeo}{dots}</p>
                <p style={{ color: "#555", fontSize: 13, marginTop: 8 }}>Pulling live call report data and scoring against your avatar…</p>
              </div>}

              {!prospecting && prospectError && (
                <div style={{ ...card, borderColor: "rgba(255,107,107,0.3)", background: "rgba(255,107,107,0.05)" }}>
                  <p style={{ color: "#ff6b6b", fontSize: 14, margin: 0 }}>⚠ {prospectError}</p>
                </div>
              )}

              {!prospecting && prospects.map(p => (
                <div key={p.cert} style={{ ...card, borderColor: expanded === p.cert ? "rgba(0,229,160,0.25)" : "rgba(255,255,255,0.08)", transition: "all 0.2s" }}>
                  {/* ── Header row ── */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer" }} onClick={() => setExpanded(expanded === p.cert ? null : p.cert)}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                        <h3 style={{ fontSize: 18, fontWeight: 600, color: "#e8e4dc", margin: 0, fontFamily: "'Playfair Display',serif" }}>{p.name}</h3>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "rgba(0,180,216,0.15)", color: "#00b4d8" }}>FDIC #{p.cert}</span>
                      </div>
                      <p style={{ color: "#666", fontSize: 13, margin: 0 }}>{p.city}, {p.state} · ${p.assets}M assets · {p.branches !== "N/A" ? `${p.branches} branches` : ""}</p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <ScoreBadge score={p.matchScore} />
                      <span style={{ color: "#555", fontSize: 18 }}>{expanded === p.cert ? "▲" : "▼"}</span>
                    </div>
                  </div>

                  {/* ── Expanded detail ── */}
                  {expanded === p.cert && (
                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.07)" }}>

                      {/* Financial metrics */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 10, marginBottom: 20 }}>
                        <MetricPill label="Assets" value={`$${p.assets}M`} />
                        <MetricPill label="ROA" value={p.roa} unit={p.roa !== "N/A" ? "%" : ""} />
                        <MetricPill label="Efficiency Ratio" value={p.efficiencyRatio} unit={p.efficiencyRatio !== "N/A" ? "%" : ""} />
                        <MetricPill label="Asset CAGR (3yr)" value={p.assetCAGR} unit={p.assetCAGR !== "N/A" ? "%/yr" : ""} />
                        <MetricPill label="Deposit CAGR (3yr)" value={p.depositCAGR} unit={p.depositCAGR !== "N/A" ? "%/yr" : ""} />
                        <MetricPill label="Branches" value={p.branches} />
                      </div>

                      {/* Address & website */}
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
                        {p.address && (
                          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "10px 16px", flex: 1, minWidth: 200 }}>
                            <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Main Office</div>
                            <div style={{ fontSize: 13, color: "#d4cfca", lineHeight: 1.5 }}>{p.address}</div>
                          </div>
                        )}
                        {p.website && (
                          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "10px 16px", flex: 1, minWidth: 200 }}>
                            <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Website</div>
                            <a href={p.website} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#00e5a0", textDecoration: "none" }} onClick={e => e.stopPropagation()}>{p.website.replace(/^https?:\/\//, "")}</a>
                          </div>
                        )}
                      </div>

                      {/* Match reasons */}
                      <p style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Why This Matches Your Avatar</p>
                      {p.matchReasons.map((r, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, marginBottom: 6 }}>
                          <span style={{ color: r.startsWith("⚠") ? "#f5c842" : "#00e5a0", flexShrink: 0 }}>{r.startsWith("⚠") ? "" : "✓"}</span>
                          <span style={{ fontSize: 13, color: r.startsWith("⚠") ? "#999" : "#aaa", lineHeight: 1.5 }}>{r}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {!prospecting && prospects.length > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 32 }}>
                  <button style={btn("ghost")} onClick={() => { setStep("anchor"); setAnalysisDone(false); setAnalysisText(""); setProspects([]); setAnchors([]); setExpanded(null); setProspectError(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}>← Start New Analysis</button>
                  <button onClick={() => {
                    const headers = ["Name","FDIC #","Street Address","City","State","Zip","Website","Total Assets","ROA (%)","Efficiency Ratio (%)","Asset CAGR 3yr (%)","Deposit CAGR 3yr (%)","Branches","Match Score","Match Reasons","Lead Status"];
                    const rows = prospects.map(p => {
                      // Split address into parts — FDIC format is "123 Main St, City, ST, 12345"
                      const addrParts = (p.address || "").split(",").map(s => s.trim());
                      const street = addrParts[0] || "";
                      const city = addrParts[1] || p.city || "";
                      const state = addrParts[2] || p.state || "";
                      const zip = addrParts[3] || "";
                      // Strip http:// https:// www. from website
                      const domain = (p.website || "").replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
                      // Convert $M to full dollar figure
                      const totalAssets = p.assets !== "N/A" ? `$${(p.assets * 1_000_000).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
                      return [
                        `"${p.name}"`,
                        p.cert,
                        `"${street}"`,
                        `"${city}"`,
                        `"${state}"`,
                        `"${zip}"`,
                        `"${domain}"`,
                        `"${totalAssets}"`,
                        p.roa,
                        p.efficiencyRatio,
                        p.assetCAGR,
                        p.depositCAGR,
                        p.branches,
                        p.matchScore,
                        `"${p.matchReasons.join("; ")}"`,
                        "Cold Outbound Opportunity",
                      ];
                    });
                    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `meridian-prospects-${targetGeo.replace(/[\s,]+/g, "-")}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }} style={{ padding: "10px 22px", borderRadius: 10, background: "linear-gradient(135deg,#00e5a0,#00c48c)", border: "none", color: "#0d0d0f", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontWeight: 700 }}>
                    ⬇ Export CSV
                  </button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </>
  );
}
